import type { SelectedElementPayload, StyleDeltas } from '~/lib/stores/element-selector';

// Neutralize characters that would terminate the marker early — `"` closes a
// quoted field (→ single quote, readable) and `]` closes the whole marker
// (→ removed) — collapse whitespace, and bound the length so a forged or
// pathological value can't corrupt or bloat the marker.
function sanitizeMarkerField(value: string, max: number): string {
  return value.replace(/"/g, "'").replace(/\]/g, '').replace(/\s+/g, ' ').trim().slice(0, max);
}

export function buildSelectedElementMarker(sel: SelectedElementPayload): string {
  const classes = sanitizeMarkerField(sel.classes, 200);
  const text = sanitizeMarkerField(sel.textSnippet, 120);

  return `[SELECTED_ELEMENT: file=${sel.file} line=${sel.line} tag=${sel.tag} classes="${classes}" text="${text}"]`;
}

export function buildStyleEditPrompt(sel: SelectedElementPayload, deltas: StyleDeltas): string {
  const lines = Object.entries(deltas).map(([prop, d]) => `- ${prop}: ${d.value} (was ${d.was})`);
  return [
    buildSelectedElementMarker(sel),
    '',
    'Apply EXACTLY these style changes to this element and nothing else,',
    'using Tailwind utilities (nearest scale token, else arbitrary value):',
    ...lines,
  ].join('\n');
}

export const DELETE_ELEMENT_INSTRUCTION =
  'Delete this element and repair the surrounding layout so no gap or broken grid remains.';

/* Agent-turn fallbacks for edits the deterministic path couldn't apply (text
 * not found verbatim, dynamic className/src, …). Each prompt starts with the
 * SELECTED_ELEMENT marker so Chat.client doesn't prepend a second one. */

export function buildTextEditFallbackPrompt(sel: SelectedElementPayload, oldText: string, newText: string): string {
  return [
    buildSelectedElementMarker(sel),
    '',
    `Change this element's text. It currently reads:`,
    oldText.slice(0, 1000),
    '',
    'Replace it with EXACTLY this text (change nothing else on the page):',
    newText.slice(0, 1000),
  ].join('\n');
}

export function buildImageSwapFallbackPrompt(sel: SelectedElementPayload, url: string): string {
  return [
    buildSelectedElementMarker(sel),
    '',
    `Replace this image so it displays EXACTLY this URL (keep sizing, alt text, and layout unchanged): ${url}`,
  ].join('\n');
}
